import { collection, doc, getDoc, getDocs, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebaseDDH';

const COLLECTION = 'clients';

// Firebase Auth emails are case-insensitive but stored as typed, so every
// email used as a key or a match is normalized through here. Quotes are
// addressed by email (see utils/quotes.js) because the client SDK can't look
// a user up by uid, so a mismatch in case would silently hide a quote.
export function normalizeEmail(email) {
  return (email || '').trim().toLowerCase();
}

// Mirrors an Auth account into Firestore so the admin dashboard has something
// to list — Auth itself can't be enumerated from the client SDK. The doc ID is
// the normalized email, so re-registering the same client updates rather than
// duplicating.
export async function recordClient(email, name = null) {
  const id = normalizeEmail(email);
  await setDoc(doc(db, COLLECTION, id), {
    email: id,
    name,
    createdAt: serverTimestamp(),
  }, { merge: true });
}

export async function listClients() {
  const snapshot = await getDocs(collection(db, COLLECTION));
  return snapshot.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    // Sorted by what's actually shown, so the dropdown reads alphabetically
    // rather than by an email that may start with anything.
    .sort((a, b) => clientName(a).localeCompare(clientName(b)));
}

// A client reading their own record — the rules allow this one document only.
export async function getClient(email) {
  const snap = await getDoc(doc(db, COLLECTION, normalizeEmail(email)));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

// The name is what identifies a client to a human; the email is the key the
// data is stored under. Everything user-facing should lead with the name and
// fall back to the email only when there isn't one.
export function clientName(client) {
  return client?.name?.trim() || client?.email || '';
}

// Internal notes, in their own admin-only collection — see firestore.rules for
// why they can't just be a field on the client record.
const NOTES_COLLECTION = 'clientNotes';

export async function getClientNotes(email) {
  const snap = await getDoc(doc(db, NOTES_COLLECTION, normalizeEmail(email)));
  return snap.exists() ? (snap.data().notes || '') : '';
}

export async function saveClientNotes(email, notes) {
  await setDoc(doc(db, NOTES_COLLECTION, normalizeEmail(email)), {
    notes: notes || '',
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

export function clientLabel(client) {
  const name = client?.name?.trim();
  return name ? `${name} — ${client.email}` : (client?.email || '');
}
